import { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useI18n } from 'iomart-hooks';
import Iconify from 'src/components/iconify';
import RHFDictionary from '../RHFDictionary';
import { useProduct } from 'iomart-hooks';
import type { Columns } from 'src/@types/common';
import { AddProductDrawer } from 'src/pages/dashboard/dictionaries/products/components/AddProductDrawer';

// Первый артикул или "-"
const firstArticle = (row: any) => {
  const arts = row?.articles;
  if (!Array.isArray(arts) || arts.length === 0) return '-';
  const first = arts[0];
  return typeof first === 'object' ? first?.article ?? '-' : String(first);
};

const DICTIONARY_COLUMNS: (Columns & { minWidth?: number; maxWidth?: number })[] = [
  {
    name: 'name',
    subname: 'shortDescription',
    label: 'Name',
    align: 'left',
    nameLines: 2,
    subNameLines: 1,
    minWidth: 160,
    width: 240,
    maxWidth: 320,
  },
  {
    name: 'articles',
    label: 'Article',
    format: (item: any, _name: string) => firstArticle(item),
    minWidth: 100,
    width: 120,
    maxWidth: 140,
  },
  {
    name: 'productCategoryData',
    subname: 'productBrandData',
    label: 'Category / Brand',
    format: 'selector',
    subFormat: 'selector',
    params: { selector: (c: any) => c?.name ?? '-' },
    nameLines: 1,
    subNameLines: 1,
    minWidth: 120,
    width: 150,
    maxWidth: 170,
  }
];

type RHFProductProps = {
  name: string;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Фильтр: только товары (isSet: false) или только комплекты (isSet: true) */
  isSet?: boolean;
  /** Фильтр: только с синхронизацией */
  withSync?: boolean;
  /** Ширина Drawer справочника */
  drawerWidth?: Record<string, string | number>;
  /** Вызывается при выборе товара (из справочника или после добавления) */
  onChange?: (value: { id: number; name?: string } | null) => void;
};

export default function RHFProduct({
  name,
  label = 'Product',
  multiple = false,
  disabled,
  isSet,
  withSync,
  drawerWidth = { xs: '100%', sm: 640 },
  onChange,
}: RHFProductProps) {
  const { t } = useI18n(false);
  const { setValue } = useFormContext();
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const filters = { ...(isSet != null && { isSet }), ...(withSync === true && { withSync: true }) };

  return (
    <>
      <RHFDictionary
        name={name}
        label={label}
        useHook={useProduct}
        columns={DICTIONARY_COLUMNS}
        multiple={multiple}
        optionsLabel="name"
        getOptionLabel={(opt: any) => opt?.name || `#${opt?.id}` || ''}
        searchName="search"
        pageSize={10}
        disabled={disabled}
        filters={filters}
        drawerWidth={drawerWidth}
        refreshTrigger={refreshTrigger}
        onChange={onChange}
        headerActions={
          <Tooltip title={t('Add product')}>
            <span>
              <IconButton
                size="small"
                onClick={() => setAddDrawerOpen(true)}
                disabled={disabled}
                aria-label={t('Add product')}
              >
                <Iconify icon="eva:plus-fill" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
      <AddProductDrawer
        open={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        onSuccess={(item) => {
          setValue(name, item, { shouldValidate: true, shouldDirty: true });
          onChange?.(item);
          setAddDrawerOpen(false);
          setRefreshTrigger((t) => t + 1);
        }}
      />
    </>
  );
}
